import { FieldLabel } from 'src/presentations/components/molecules/fields/FieldLabel';
import { FC, useContext, useEffect } from 'react';
import { FieldValues } from 'react-hook-form';
import { SelectElementProps, useFormContext, useWatch } from 'react-hook-form-mui';
import { beautifyEnum, isJsonString } from 'src/utils/utility';
import { useCalculateSubCharge, ChargeDomain, LegType } from 'src/use-cases/invoice/useQuestionnaire';
import { ContractChargeDetailDto } from 'src/generated/services/TFinancialApi';
import { SelectedDeviationContext } from 'src/presentations/components/organisms/actionDetails/mainDetails/deviations/SelectedDeviationContext';

import { useShipmentSelector } from 'src/presentations/components/organisms/actionDetails/ShipmentSelector';
import { ChargeSelectionList } from '../../charges/ChargeSelectionList';

import { useDefaultProps } from '../useDefaultProps';

export interface IRhfFieldSelectSubChargeDomainProps extends SelectElementProps<FieldValues> {
  // eslint-disable-next-line react/no-unused-prop-types
  hideFieldLabel?: boolean;
  // eslint-disable-next-line react/no-unused-prop-types
  answerId: string;
}

interface NewChargeSelectOption {
  answerOptionId: string;
  answerText: string;
}

// eslint-disable-next-line react-refresh/only-export-components
export function isNewChargeSelectOption(obj: any): obj is NewChargeSelectOption {
  return typeof obj === 'object' && obj !== null && typeof obj.answerOptionId === 'string' && typeof obj.answerText === 'string';
}

export const RhfFieldSelectSubChargeDomain: FC<IRhfFieldSelectSubChargeDomainProps> = (props) => {
  const { label, hideFieldLabel, answerId, name, isReadOnly } = { ...props, ...useDefaultProps(props) };
  const { selectedDeviation } = useContext(SelectedDeviationContext);
  const { shipment } = useShipmentSelector();
  const { setValue, formState } = useFormContext();
  const value = useWatch({ name });

  // Get legType and chargeDomain from form values
  const legType = useWatch({ name: 'legType' }) as LegType;
  const chargeDomain = useWatch({ name: 'chargeDomain' }) as ChargeDomain;

  // Check if this is NRCF: contractId === null OR shippingServiceId === null
  const isNrcf = !shipment?.contractId || !shipment?.shippingServiceId;

  // Get subChargeDomains using the hook directly - skip for NRCF
  const { data: subChargeDomains } = useCalculateSubCharge(selectedDeviation?.id, legType, chargeDomain, isNrcf);

  useEffect(() => {
    if (typeof value === 'string') {
      if (!isJsonString(value)) {
        setValue(name, JSON.stringify({ answerOptionId: answerId, answerText: value }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answerId, name, value]);

  // Get current selected value
  let selectedCharge: ContractChargeDetailDto | null = null;
  let selectedChargeName: string | null = null;

  if (isJsonString(value)) {
    const parsed = JSON.parse(value);
    if (isNewChargeSelectOption(parsed)) {
      selectedChargeName = parsed.answerText;
      selectedCharge = subChargeDomains?.find((domain) => domain.chargeName === parsed.answerText) || null;
    }
  }

  // Handle charge selection
  const handleChargeSelect = (charge: ContractChargeDetailDto) => {
    const newValue = JSON.stringify({ answerOptionId: answerId, answerText: charge.chargeName });
    setValue(name, newValue);
    // No need for manual flag - React Hook Form validates automatically
  };

  // Get error message from form state
  const error = formState.errors[name];
  const errorMessage = error?.message as string | undefined;

  if (isReadOnly) {
    // Show the charge name from the stored value, even if subChargeDomains is not loaded
    const displayValue = selectedChargeName ? beautifyEnum(selectedChargeName) : 'No charge selected';
    return <FieldLabel label={!hideFieldLabel && label}>{displayValue}</FieldLabel>;
  }

  return (
    <FieldLabel label={!hideFieldLabel && label}>
      <div>
        <ChargeSelectionList
          charges={subChargeDomains || []}
          selectedCharge={selectedCharge}
          onChargeSelect={handleChargeSelect}
          mode='selection'
          title={(label as string) || 'Select Charge Type'}
          showHeader
          maxHeight='max-h-400'
          emptyMessage='No charges available'
          emptySubMessage='Please select leg type and charge domain first'
        />
        {errorMessage && <p className='text-red-40 text-10 xl:text-12 mt-12'>{errorMessage}</p>}
      </div>
    </FieldLabel>
  );
};
